import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import {
  PLANS as DEFAULT_PHONE_PLANS,
  MOBILE_INTERNET_PLANS as DEFAULT_MI_PLANS,
  EXTRA_INTERNET_CODES as DEFAULT_CODES,
  BRAND as DEFAULT_BRAND,
} from "@/data/plans";

const ContentContext = createContext(null);

// Domyślna treść z pliku plans.js (fallback zanim wczyta się z API)
const DEFAULT_PAGES = {
  internet: { eyebrow: "", hero_title: "", hero_subtitle: "", bullets: [], cta_button: "", show_featured: true },
  regulamin: { title: "", subtitle: "", body: "" },
  extra_internet: { eyebrow: "", title: "", subtitle: "", col_code: "", col_size: "", col_price: "", note_bottom: "" },
  contact: { eyebrow: "", title: "", subtitle: "" },
  order_sim: { eyebrow: "", title: "", subtitle: "", success_title: "", success_message: "", number_type_title: "Numer telefonu", number_type_new_label: "Nowy numer telefonu", number_type_port_label: "Przenieś numer telefonu", number_type_port_help: "Podaj numer, który chcesz przenieść do naszej sieci." },
  port_number: { eyebrow: "", title: "", subtitle: "", success_title: "", success_message: "" },
  cookie_policy: { title: "", subtitle: "", body: "" },
  rodo: { title: "", subtitle: "", intro: "", body: "" },
  payment: { success_title: "", success_message: "", cancel_title: "", cancel_message: "" },
};

const DEFAULTS = {
  phone_plans: DEFAULT_PHONE_PLANS,
  mobile_internet_plans: DEFAULT_MI_PLANS,
  internet_codes: DEFAULT_CODES,
  brand: DEFAULT_BRAND,
  pages: DEFAULT_PAGES,
};

export function ContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULTS);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      if (data && Array.isArray(data.phone_plans)) {
        setContent({
          phone_plans: data.phone_plans,
          mobile_internet_plans: data.mobile_internet_plans || [],
          internet_codes: data.internet_codes || [],
          // name pozostaje stałe (marka), reszta z API
          brand: data.brand ? { name: DEFAULT_BRAND.name, ...data.brand } : DEFAULT_BRAND,
          pages: data.pages ? {
            internet: { ...DEFAULT_PAGES.internet, ...(data.pages.internet || {}) },
            regulamin: { ...DEFAULT_PAGES.regulamin, ...(data.pages.regulamin || {}) },
            extra_internet: { ...DEFAULT_PAGES.extra_internet, ...(data.pages.extra_internet || {}) },
            contact: { ...DEFAULT_PAGES.contact, ...(data.pages.contact || {}) },
            order_sim: { ...DEFAULT_PAGES.order_sim, ...(data.pages.order_sim || {}) },
            port_number: { ...DEFAULT_PAGES.port_number, ...(data.pages.port_number || {}) },
            cookie_policy: { ...DEFAULT_PAGES.cookie_policy, ...(data.pages.cookie_policy || {}) },
            rodo: { ...DEFAULT_PAGES.rodo, ...(data.pages.rodo || {}) },
            payment: { ...DEFAULT_PAGES.payment, ...(data.pages.payment || {}) },
          } : DEFAULT_PAGES,
        });
      }
    } catch {
      // zostawiamy wartości domyślne
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <ContentContext.Provider value={{ ...content, refresh }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  return useContext(ContentContext) || { ...DEFAULTS, refresh: () => {} };
}
